import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import NotificationsIcon from '@mui/icons-material/Notifications';
import RightDrawer from "../reusable-ui/drawer-modal";
import { NotificationDataType, NotificationStateType, getAllNotifications, markAllAsRead, markReadNotification, updatePendingNotifications } from "../../redux/slices/notifications";
import { useDispatch } from "react-redux";
import { NOTIFICATION_EVENT_TYPES } from "../../helpers/notification-helpers";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { throttle } from "lodash";
import RenderReconNotification from "./recon-notification-card";

export enum NotificationStatusUpdates {
  read = "Read"
}
export const NOTIFICATION_TYPES_TO_SHOW: string[] = [NOTIFICATION_EVENT_TYPES.ONLINE_ORDER_RECONCILIATION_NOTIFICATION];

export default function NotificationsView() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const unreadNotifications = useSelector((state: RootState) => state.notificationsReducer.notifications.results.filter((notification: NotificationDataType) => notification.status !== NotificationStatusUpdates.read));

  function toggleSheet() {
    setSheetOpen(!sheetOpen);
  }

  return <>
    <NotificationIcon notificationCount={unreadNotifications.length} onToggle={toggleSheet} />
    {sheetOpen && <RightDrawer title="Notifications" width={700} handleClose={toggleSheet}>
      <NotificationsList />
    </RightDrawer>}
  </>
}

// function NotificationIcon({ onToggle, notificationCount }: { onToggle: () => void; notificationCount: number }) {
//   return <div onClick={onToggle} className="flex items-center gap-[8px] cursor-pointer">
//     <div className="relative">
//       <NotificationsIcon />
//       {!!notificationCount && <div className="bg-[#FF4040] rounded-[100%] absolute top-[7px] right-[0] w-[5px] h-[5px]">&nbsp;</div>}
//     </div>
//     <div className="text-14 font-bold">Notifications</div>
//   </div>
// }


function NotificationIcon({ onToggle, notificationCount }: { onToggle: () => void; notificationCount: number }) {
  return <div title="Notifications"
    className={`text-18 border-l border-r flex lg:w-[68px] md:w-[72px] h-[100%] items-center justify-center hover:bg-[#EBECF1] cursor-pointer`}
    onClick={onToggle}
  > <NotificationsIcon sx={{ fontSize: "28px" }} />
    {!!notificationCount && <div className="notify-number bg-[#397EF5] flex items-center justify-center h-20 px-2 rounded-sm ml-2">
      <span className="text-15 font-inter font-semibold text-white">
        {notificationCount}
      </span>
    </div>}
  </div>
}

function NotificationsList() {
  const { lastKey, results }: NotificationStateType = useSelector((state: RootState) => state.notificationsReducer.notifications);
  const listRef = useRef(null);
  const { isAtBottom } = useInfiniteScroll(listRef);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updatePendingNotifications(false));
    dispatch(getAllNotifications({ pageSize: 10, eventTypes: NOTIFICATION_TYPES_TO_SHOW }));
  }, []);


  function handleReadAll() {
    dispatch(markAllAsRead());
  }


  const fetchThrottled = useMemo(() => {
    const throttledFn = throttle(() => {
      dispatch(getAllNotifications({ pageSize: 10, eventTypes: NOTIFICATION_TYPES_TO_SHOW, lastKey }));
    }, 1000);

    return throttledFn;
  }, [lastKey]);

  useEffect(() => {
    return () => {
      fetchThrottled.cancel();
    };
  }, [fetchThrottled]);

  useEffect(() => {
    if (lastKey && isAtBottom) {
      fetchThrottled();
    }
  }, [isAtBottom, lastKey, fetchThrottled]);

  const handleReadMessage = useCallback((messageId: string) => {
    dispatch(
      markReadNotification({
        messageIds: [messageId],
        status: NotificationStatusUpdates.read,
      })
    );
  }, [])

  if (!results.length) {
    return <NoNotifications />
  }

  return <div className="font-inter flex flex-col h-[100%]">
    <Subheader handleMarkDone={handleReadAll} pendingRead={!!results.length} />
    <div ref={listRef} className="flex flex-col overflow-scroll flex-1 bg-[#eff3fa] px-[20px]">
      {results.map((message) => <MapRenderNotification value={message} onRead={handleReadMessage} />)}
    </div>
  </div>
}

function MapRenderNotification({ value, onRead }: { value: NotificationDataType, onRead: (messageId: string) => void }) {
  switch (value.eventType) {
    case NOTIFICATION_EVENT_TYPES.ONLINE_ORDER_RECONCILIATION_NOTIFICATION:
      return <RenderReconNotification onRead={onRead} data={value} />
  }
  return <></>
}

function Subheader({ handleMarkDone, pendingRead }: { handleMarkDone: () => void; pendingRead: boolean }) {
  return <div className="flex w-full justify-between items-center px-20 py-[14px] border-b">
    {pendingRead ? <div
      onClick={handleMarkDone}
      className="text-[#397EF5] text-[18px] font-medium cursor-pointer flex content-center"
    >
      <div className="rista-new-sprite mark-done-icon mr-2"></div>
      Mark all as read
    </div> : <div className="text-[18px] font-medium cursor-pointer flex content-center">
      <div className="rista-new-sprite marked-read-icon mr-8"></div>
      All Notification marked read
    </div>}
  </div>
}

function NoNotifications() {
  return <div className="flex flex-col items-center justify-center h-2/3 text-[18px] font-semibold">
    <div className="rista-new-sprite no-item-image mb-[16px]"></div>
    <div>No Notifications</div>
  </div>
}